import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAppResource } from '../../context/AppDataContext';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import BulkImportPanel from '../../components/BulkImportPanel';
import { toast } from '../../utils/toast';
import {
  canManageUser,
  canSetManagerFlag,
  getAssignableRoles,
  getRoleLabel,
  getRoleNameLabel,
  isLockedSuperAdmin,
} from '../../utils/permissions';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import './Users.css';
import { PageLoader } from '../../components/Loader';

// Clients are managed on the Clients page; this list is the audit staff.
const STAFF_ROLE_NAMES = ['super_admin', 'admin', 'auditor'];

const BULK_COLUMNS = [
  { key: 'name', label: 'Name', aliases: ['full name', 'user name'] },
  { key: 'email', label: 'Email', required: true, aliases: ['email id', 'e mail', 'mail'] },
  {
    key: 'role',
    label: 'Role',
    aliases: ['user type', 'type'],
    hint: 'Scanning Member (default), Data ASA or Admin',
  },
  { key: 'mpin', label: 'MPIN', aliases: ['pin', 'mpin code'], hint: '4–6 digits, needed for Scanning Members and Data ASAs to sign in to the app', secret: true },
  { key: 'password', label: 'Password', hint: 'optional, at least 6 characters', secret: true },
];

const BULK_SAMPLE = [
  { name: 'Ravi Kumar', email: 'ravi@example.com', role: 'Scanning Member', mpin: '4821', password: '' },
  { name: 'Asha Patel', email: 'asha@example.com', role: 'Data ASA', mpin: '7310', password: '' },
];

const emptyForm = {
  email: '',
  name: '',
  provider: 'google',
  role_id: '',
  is_manager: false,
  password: '',
  mpin: '',
  clear_mpin: false,
};

function Users({ embedded = false }) {
  const { user: currentUser } = useAuth();
  const loadUsers = useCallback(() => userService.getAll(), []);
  const loadRoles = useCallback(() => roleService.getAll(), []);
  const {
    data: users = [],
    loading: usersLoading,
    error: usersError,
    refresh: refreshUsers,
  } = useAppResource('users', loadUsers, []);
  const {
    data: roles = [],
    loading: rolesLoading,
    error: rolesError,
  } = useAppResource('roles', loadRoles, []);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const assignableRoles = useMemo(
    () => getAssignableRoles(currentUser, roles).filter((role) => role.name !== 'client'),
    [currentUser, roles]
  );

  const staffUsers = useMemo(
    () => users.filter((user) => !user.role_name || STAFF_ROLE_NAMES.includes(user.role_name)),
    [users]
  );

  const toBulkPayload = useCallback(
    (record) => {
      const email = record.email.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { error: 'Missing or invalid email' };
      }
      const roleText = (record.role || 'auditor').trim().toLowerCase();
      const isManager = ['data asa', 'asa', 'data_asa', 'manager', 'team lead', 'team_lead', 'teamlead', 'auditor manager', 'lead'].includes(roleText);
      const roleName = isManager
        ? 'auditor'
        : ['scanner', 'scanning member', 'scanning_member', 'member'].includes(roleText)
          ? 'auditor'
          : roleText === 'super admin'
            ? 'super_admin'
            : roleText;
      const role = assignableRoles.find((item) => item.name === roleName);
      if (!role) {
        return { error: `Role "${record.role}" is not allowed here (use Scanning Member, Data ASA or Admin)` };
      }
      if (isManager && !canSetManagerFlag(currentUser)) {
        return { error: 'You cannot create Data ASA users' };
      }
      const mpin = record.mpin.replace(/\s/g, '');
      if (mpin && !/^\d{4,6}$/.test(mpin)) {
        return { error: 'MPIN must be 4 to 6 digits' };
      }
      if (record.password && record.password.length < 6) {
        return { error: 'Password must be at least 6 characters' };
      }
      if (roleName === 'auditor' && !mpin && !record.password) {
        return { error: 'Scanning Members and Data ASAs need an MPIN (or password) to sign in' };
      }
      const payload = {
        email,
        name: record.name || '',
        role_id: Number(role.id),
        is_manager: isManager,
        provider: record.password ? 'password' : 'google',
      };
      if (mpin) payload.mpin = mpin;
      if (record.password) payload.password = record.password;
      return { payload };
    },
    [assignableRoles, currentUser]
  );

  // Bulk upload modal: the row check and the upload (one result per row).
  const checkBulkUser = useCallback((record) => toBulkPayload(record).error || '', [toBulkPayload]);
  const uploadBulkUsers = useCallback(
    async (records) => {
      const response = await userService.bulkCreate(records.map((record) => toBulkPayload(record).payload));
      const results = (response?.results || []).map((item, index) => {
        const line = records[item.index ?? index]?.line;
        if (item.ok) return { line, status: 'created' };
        const message = item.error || item.message || 'Not added';
        return { line, status: /already|exists|registered/i.test(message) ? 'skipped' : 'error', message };
      });
      return {
        created: results.filter((r) => r.status === 'created').length,
        skipped: results.filter((r) => r.status === 'skipped').length,
        errors: results.filter((r) => r.status === 'error').length,
        results,
      };
    },
    [toBulkPayload],
  );

  const selectedRoleName = useMemo(() => {
    const role = roles.find((item) => String(item.id) === String(form.role_id));
    return role?.name || '';
  }, [form.role_id, roles]);

  const loading = usersLoading || rolesLoading;
  const visibleError = error || usersError || rolesError;

  const openCreateForm = () => {
    setEditingUser(null);
    setForm({
      ...emptyForm,
      role_id: assignableRoles[0] ? String(assignableRoles[0].id) : '',
    });
    setFormOpen(true);
  };

  const openEditForm = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email || '',
      name: user.name || '',
      provider: user.provider || 'google',
      role_id: user.role_id ? String(user.role_id) : '',
      is_manager: Boolean(user.is_manager),
      password: '',
      mpin: '',
      clear_mpin: false,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingUser(null);
    setForm(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      // MPIN accepts digits only, max 6.
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'mpin'
            ? value.replace(/\D/g, '').slice(0, 6)
            : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      email: form.email,
      name: form.name,
      provider: form.provider,
      role_id: form.role_id ? Number(form.role_id) : null,
      is_manager: selectedRoleName === 'auditor' ? form.is_manager : false,
    };

    if (form.password) {
      payload.password = form.password;
    }

    if (form.mpin && !/^\d{4,6}$/.test(form.mpin)) {
      toast.error('User not saved', 'MPIN must be 4 to 6 digits.');
      setSaving(false);
      return;
    }

    if (form.clear_mpin) {
      payload.clear_mpin = true;
    } else if (form.mpin) {
      payload.mpin = form.mpin;
    }

    if (
      !editingUser &&
      selectedRoleName === 'auditor' &&
      !form.mpin &&
      !form.password
    ) {
      toast.error('User not saved', 'Set an MPIN so this person can sign in to the mobile app.');
      setSaving(false);
      return;
    }

    try {
      if (editingUser) {
        await userService.update(editingUser.id, payload);
        toast.success('User updated', payload.email || editingUser.email);
      } else {
        await userService.create(payload);
        toast.success('User added', payload.email);
      }
      closeForm();
      await refreshUsers();
    } catch (err) {
      toast.error(editingUser ? 'User not updated' : 'User not added', err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!canManageUser(currentUser, user)) {
      return;
    }

    const confirmed = window.confirm(`Delete user ${user.email}?`);
    if (!confirmed) {
      return;
    }

    setError('');

    try {
      await userService.delete(user.id);
      toast.success('User deleted', user.email);
      await refreshUsers();
    } catch (err) {
      toast.error('User not deleted', err.message);
    }
  };

  const actions = assignableRoles.length > 0 && (
    <div className="users-page__actions">
      <button
        type="button"
        className="users-page__add-btn users-page__add-btn--ghost"
        onClick={() => setBulkOpen(true)}
      >
        Bulk add users
      </button>
      <button type="button" className="users-page__add-btn" onClick={openCreateForm}>
        + Add User
      </button>
    </div>
  );

  const columns = [
    { headerName: 'Name', field: 'name', valueFormatter: (p) => dash(p.value), minWidth: 170 },
    { headerName: 'Email', field: 'email', minWidth: 220 },
    {
      headerName: 'Role',
      colId: 'role',
      valueGetter: (p) => getRoleLabel(p.data),
      minWidth: 150,
      cellRenderer: (p) => (
        <span className={`role-badge role-badge--${p.data.role_name || 'none'}`}>{p.value}</span>
      ),
    },
    { headerName: 'Provider', colId: 'provider', valueGetter: (p) => p.data.provider || 'google', maxWidth: 140 },
    {
      headerName: 'MPIN',
      colId: 'mpin',
      maxWidth: 130,
      valueGetter: (p) =>
        p.data.role_name === 'auditor' ? (p.data.has_mpin ? 'Set' : 'Not set') : '—',
      cellRenderer: (p) =>
        p.data.role_name === 'auditor' ? (
          <span
            className={`mpin-badge ${p.data.has_mpin ? 'mpin-badge--set' : 'mpin-badge--missing'}`}
            title={
              p.data.mpin_updated_at
                ? `Last changed ${new Date(p.data.mpin_updated_at).toLocaleString()}`
                : undefined
            }
          >
            {p.value}
          </span>
        ) : (
          '—'
        ),
    },
    actionsColumn((user) => {
      const canEdit = canManageUser(currentUser, user);
      const canDelete = canEdit && !isLockedSuperAdmin(user);
      return (
        <>
          <button
            type="button"
            className="users-table__btn users-table__btn--edit"
            onClick={() => openEditForm(user)}
            disabled={!canEdit}
            title={
              isLockedSuperAdmin(user)
                ? 'Super admin user cannot be edited'
                : canEdit
                  ? 'Edit user'
                  : 'You cannot edit this user'
            }
          >
            Edit
          </button>
          <button
            type="button"
            className="users-table__btn users-table__btn--delete"
            onClick={() => handleDelete(user)}
            disabled={!canDelete}
            title={
              isLockedSuperAdmin(user)
                ? 'Super admin user cannot be deleted'
                : canDelete
                  ? 'Delete user'
                  : 'You cannot delete this user'
            }
          >
            Delete
          </button>
        </>
      );
    }, { className: 'users-table__actions' }),
  ];

  return (
    <div className={embedded ? 'users-page users-page--embedded' : 'page users-page'}>
      {embedded ? (
        <div className="users-page__toolbar">
          <div>
            <h2>Users</h2>
            <p className="users-page__subtitle">
              Audit staff: Admins, Data ASAs and Scanning Members. Clients are on the
              Clients page.
            </p>
          </div>
          {actions}
        </div>
      ) : (
        <header className="page__header users-page__header">
          <div>
            <p className="page__eyebrow">Management</p>
            <h1>Users</h1>
            <p className="users-page__subtitle">
              Hierarchy: Super Admin → Admin → Data ASA → Scanning Member
            </p>
          </div>
          {actions}
        </header>
      )}

      {visibleError && (
        <div className="users-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      <section className="users-page__card">
        {loading ? (
          <PageLoader label="Loading users…" />
        ) : staffUsers.length === 0 ? (
          <p className="users-page__empty">No users yet.</p>
        ) : (
          <DataGrid
            rowData={staffUsers}
            columnDefs={columns}
            searchPlaceholder="Search name, email or role…"
          />
        )}
      </section>

      {bulkOpen ? (
        <BulkImportPanel
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          title="Bulk add users"
          noun="user"
          nounPlural="users"
          intro="Add many Scanning Members, Data ASAs or Admins at once. Each row becomes one user; users whose email is already registered are not added."
          columns={BULK_COLUMNS}
          sampleRows={BULK_SAMPLE}
          templateName="auditor-roster-users.csv"
          check={checkBulkUser}
          upload={uploadBulkUsers}
          onDone={refreshUsers}
        />
      ) : null}

      {formOpen && (
        <div className="users-modal">
          <div className="users-modal__backdrop" onClick={closeForm} />
          <div className="users-modal__panel" role="dialog" aria-modal="true">
            <div className="users-modal__header">
              <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button type="button" onClick={closeForm}>×</button>
            </div>

            <form className="users-form" onSubmit={handleSubmit}>
              <label>
                Email
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Name
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                />
              </label>

              <label>
                Role
                <select name="role_id" value={form.role_id} onChange={handleChange} required>
                  <option value="">Select role</option>
                  {assignableRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {getRoleNameLabel(role.name)}
                    </option>
                  ))}
                </select>
              </label>

              {selectedRoleName === 'auditor' && canSetManagerFlag(currentUser) && (
                <label className="users-form__checkbox">
                  <input
                    type="checkbox"
                    name="is_manager"
                    checked={form.is_manager}
                    onChange={handleChange}
                  />
                  <span>Data ASA (team lead: can add and remove Scanning Members)</span>
                </label>
              )}

              {selectedRoleName === 'auditor' && (
                <>
                  <label>
                    {editingUser
                      ? editingUser.has_mpin
                        ? 'New MPIN (leave blank to keep current)'
                        : 'MPIN (4–6 digits)'
                      : 'MPIN (4–6 digits)'}
                    <input
                      type="password"
                      name="mpin"
                      value={form.mpin}
                      onChange={handleChange}
                      inputMode="numeric"
                      pattern="\d{4,6}"
                      minLength={4}
                      maxLength={6}
                      required={!editingUser}
                      disabled={form.clear_mpin}
                      autoComplete="new-password"
                      placeholder="e.g. 4821"
                    />
                    <span className="users-form__hint">
                      They sign in to the mobile app with their email and this MPIN.
                      Share it with them directly.
                    </span>
                  </label>

                  {editingUser?.has_mpin && (
                    <label className="users-form__checkbox">
                      <input
                        type="checkbox"
                        name="clear_mpin"
                        checked={form.clear_mpin}
                        onChange={handleChange}
                      />
                      <span>Remove MPIN (blocks mobile sign-in until a new one is set)</span>
                    </label>
                  )}

                  <label>
                    {editingUser ? 'New password (optional)' : 'Password (optional)'}
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </label>
                </>
              )}

              <label>
                Provider
                <select name="provider" value={form.provider} onChange={handleChange}>
                  <option value="google">Google</option>
                  <option value="password">Password (mobile app)</option>
                </select>
              </label>

              <div className="users-form__actions">
                <button type="button" className="users-form__cancel" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="users-form__submit" disabled={saving}>
                  {saving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
